import { logger } from '@/shared/utils';
import PropTypes from 'prop-types';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { msalConfig } from '../../features/auth/eim/config/msalConfig';
import { EIM } from '@/infrastructure/sdk';
import { handleSSORegistration } from '../../features/auth/eim/services/ssoService';
import { i18n } from '@/localization/i18n';
import { hideFullPageLoader, showFullPageLoader } from '@/components/FullPageLoader/fullPageLoaderService';

// MSAL requires the Web Crypto API (crypto.subtle), which browsers only expose
// on secure contexts (HTTPS / localhost). On a plain-HTTP origin (e.g. a LAN IP
// like http://172.16.x.x:3000) crypto.subtle is undefined and the MSAL
// constructor throws synchronously — which would crash the whole app at module
// load. Guard it: if MSAL can't initialise, Outlook SSO is simply disabled and
// the rest of the app (email/OTP/Google login) still works.
const canUseMsal = typeof window !== 'undefined' && window.isSecureContext && !!window.crypto?.subtle;

let msalInstance = null;
try {
   if (canUseMsal) {
      msalInstance = new PublicClientApplication(msalConfig);
   } else {
      logger.warn('MSAL/Outlook SSO disabled: insecure context (crypto.subtle unavailable).');
   }
} catch (error) {
   logger.error('MSAL initialisation failed; Outlook SSO disabled:', error);
}

export const AuthProvider = ({ children }) => {
   const isInitializedRef = useRef(false);

   useEffect(() => {
      if (isInitializedRef.current) return;
      isInitializedRef.current = true;

      // No MSAL instance (insecure context / init failed) → skip Outlook SSO
      // wiring entirely; the rest of the app continues to work.
      if (!msalInstance) return;

      const initializeMsal = async () => {
         try {
            await msalInstance.initialize();
            const response = await msalInstance.handleRedirectPromise();

            if (response) {
               showFullPageLoader('SIGN_IN_LOADING');
               const token = response.accessToken;
               const homeAccountId = response.account.homeAccountId.split('.')[0].replace(/-/g, '');
               const result = await EIM.validateSSOLogin(token, 'outlook');
               if (result.statusCode === 200) {
                  const userId = result.isExistingUser ? result.username : homeAccountId;
                  await handleSSORegistration({ id: userId, email: response.account.username });
               } else {
                  toast.error(result.message || i18n.t('toast.SSO_VALIDATION_FAILED'));
               }
            }
         } catch (error) {
            logger.error('Outlook SSO redirect/auth handling failed:', error);
            toast.error(i18n.t('toast.OUTLOOK_SSO_FAILED'));
         } finally {
            hideFullPageLoader('SIGN_IN_LOADING');
         }
      };

      initializeMsal();
   }, []);

   // Without a valid MSAL instance (insecure context / init failed), MsalProvider
   // would throw on a null instance — so render children directly and skip the
   // Outlook SSO wrapper. This is what lets the app load over a plain-HTTP LAN IP
   // (e.g. http://172.16.x.x:3001) when sharing to another device.
   if (!msalInstance) return children;

   return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

AuthProvider.propTypes = {
   children: PropTypes.node.isRequired,
};
